import { FilterOptionList, formatBriefStatus } from '@yeshuman/sdk'
import type { FilterDefinition } from '@yeshuman/sdk'

export type RecruiterBriefStatus = 'draft' | 'active' | 'closed'

export type RecruiterBriefsFilterState = {
  search: string
  status: RecruiterBriefStatus | ''
}

export type RecruiterBriefsFilterCtx = {
  counts: Record<'all' | RecruiterBriefStatus, number>
}

export const RECRUITER_BRIEF_STATUS_OPTIONS: Array<{
  value: 'all' | RecruiterBriefStatus
  label: string
}> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
]

function statusOptionLabel(
  option: { value: 'all' | RecruiterBriefStatus; label: string },
  counts: RecruiterBriefsFilterCtx['counts'],
): string {
  if (option.value === 'all') return option.label
  const count = counts[option.value]
  return count > 0 ? `${option.label} (${count})` : option.label
}

function visibleStatusOptions(counts: RecruiterBriefsFilterCtx['counts']) {
  return RECRUITER_BRIEF_STATUS_OPTIONS.filter(
    (option) => option.value === 'all' || counts[option.value] > 0,
  )
}

export function computeRecruiterBriefStatusCounts(
  briefs: { status: string }[],
): RecruiterBriefsFilterCtx['counts'] {
  let draft = 0
  let active = 0
  let closed = 0
  for (const brief of briefs) {
    if (brief.status === 'draft') draft += 1
    else if (brief.status === 'active') active += 1
    else if (brief.status === 'closed') closed += 1
  }
  return {
    all: briefs.length,
    draft,
    active,
    closed,
  }
}

export const RECRUITER_BRIEFS_FILTER_DEFINITIONS: FilterDefinition<
  RecruiterBriefsFilterState,
  RecruiterBriefsFilterCtx
>[] = [
  {
    key: 'status',
    label: 'Status',
    displayValue: (filters, ctx) => {
      const value = filters.status || 'all'
      const option = RECRUITER_BRIEF_STATUS_OPTIONS.find((opt) => opt.value === value)
      return option ? statusOptionLabel(option, ctx.counts) : 'All'
    },
    isNonDefault: (filters) => Boolean(filters.status),
    reset: (filters) => ({ ...filters, status: '' }),
    renderPopover: ({ filters, onChange, ctx }) => (
      <FilterOptionList
        type="single"
        options={visibleStatusOptions(ctx.counts).map((option) => ({
          value: option.value,
          label: statusOptionLabel(option, ctx.counts),
        }))}
        value={filters.status || 'all'}
        onValueChange={(value) =>
          onChange({
            ...filters,
            status: value === 'all' ? '' : (value as RecruiterBriefStatus),
          })
        }
      />
    ),
  },
]

export function recruiterBriefStatusLabel(status: RecruiterBriefStatus | ''): string {
  if (!status) return 'all briefs'
  return formatBriefStatus(status).toLowerCase()
}
